/**
 * API Error Utilities
 *
 * Provides helpers to classify API errors and extract user-friendly messages.
 * The Axios error interceptor in useApi.ts already enriches error.message with
 * the server-provided message, so we leverage that here.
 *
 * NOTE: Unlike tms-app-soho, this app does NOT use i18n/i18next — all messages
 * are plain English strings.
 */
import axios from 'axios';

/**
 * Known technical patterns in error messages mapped to user-friendly messages.
 */
const TECHNICAL_MESSAGE_MAP: Array<{ pattern: RegExp; message: string }> = [
  { pattern: /P2025/i, message: 'The requested record was not found' },
  { pattern: /ECONNREFUSED/i, message: 'Unable to connect to the server' },
  { pattern: /ETIMEDOUT|ECONNABORTED/i, message: 'The request timed out' },
  { pattern: /network\s?error/i, message: 'A network error occurred. Please check your connection' },
];

/**
 * Map HTTP status codes to user-friendly messages for errors that lack a server-provided message.
 */
const STATUS_CODE_MAP: Record<number, string> = {
  500: 'An unexpected server error occurred',
  502: 'An unexpected server error occurred',
  503: 'The service is temporarily unavailable',
  429: 'Too many requests. Please try again later',
};

/**
 * Check if an error is a 404 Not Found response.
 */
export function isNotFoundError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 404;
}

/**
 * Check if an error is a 404 that indicates a resource is not yet configured
 * (vs. a genuinely missing entity). This is a semantic alias for readability.
 */
export function isNotConfiguredError(err: unknown): boolean {
  return isNotFoundError(err);
}

/**
 * Regex matching UUIDs (hyphenated or non-hyphenated) that may appear in error messages.
 * Users don't need to see internal IDs like hospital_id or person_id.
 */
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b|\b[0-9a-f]{32}\b/gi;

/**
 * Sanitize a technical error message into something a non-technical user can understand.
 * - Strips UUIDs that are meaningless to end users
 * - Replaces known technical codes with friendly text
 * - Cleans up leftover artifacts (double spaces, trailing prepositions)
 */
function sanitizeMessage(message: string): string {
  if (!message) return message ?? '';

  for (const { pattern, message: friendly } of TECHNICAL_MESSAGE_MAP) {
    if (pattern.test(message)) {
      return friendly;
    }
  }

  // Strip UUIDs and clean up surrounding text
  let cleaned = message.replace(UUID_PATTERN, '').trim();
  // Remove dangling prepositions/articles left after UUID removal (e.g. "for ", "with id ")
  cleaned = cleaned.replace(/\b(for|with id|id:|id)\s*$/i, '').trim();
  // Collapse multiple spaces
  cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
  // Remove trailing punctuation artifacts
  cleaned = cleaned.replace(/[:\s"']+$/, '').trim();

  return cleaned || message;
}

/**
 * Extract a user-friendly error message from an error.
 *
 * Priority:
 * 1. Server-provided message from the API response body (already enriched by interceptor)
 * 2. Sanitized technical message (if it contains codes like P2025, ECONNREFUSED)
 * 3. Provided fallback string
 *
 * @param err - The caught error
 * @param fallback - Plain string to use if nothing better is available
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  let message = fallback;

  if (axios.isAxiosError(err)) {
    const serverMessage =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message;

    if (serverMessage && serverMessage !== 'Request failed with status code ' + err.response?.status) {
      message = serverMessage;
    } else if (err.response?.status && STATUS_CODE_MAP[err.response.status]) {
      return STATUS_CODE_MAP[err.response.status];
    }
  } else if (err instanceof Error && err.message) {
    message = err.message;
  }

  return sanitizeMessage(message);
}

/**
 * Handle a 404 error silently — returns true if the error was a 404 (caller should
 * treat as empty/not-configured), false if it's a different error that needs handling.
 *
 * Usage:
 * ```ts
 * } catch (err) {
 *   if (handleSilent404(err, 'Products')) return;
 *   toast.danger(getApiErrorMessage(err, 'Failed to load products'));
 * }
 * ```
 */
export function handleSilent404(err: unknown, context: string): boolean {
  if (isNotFoundError(err)) {
    console.info(`[${context}] Not configured (404) — using empty defaults`);
    return true;
  }
  return false;
}
